// Copyright (c) 2021-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import { useTranslation } from "react-i18next";
import {
    Navigate,
    Outlet,
    useRoutes,
} from "react-router-dom";
import { map } from "../../../../../io/hyperify/core/functions/map";
import { LogService } from "../../../../../io/hyperify/core/LogService";
import { Product } from "../../../../../io/hyperify/core/store/types/product/Product";
import { ProductType } from "../../../../../io/hyperify/core/store/types/product/ProductType";
import { TranslationFunction } from "../../../../../io/hyperify/core/types/TranslationFunction";
import { useDefaultLanguage } from "../../../../../io/hyperify/frontend/hooks/useDefaultLanguage";
import { useI18nWithLanguageService } from "../../../../../io/hyperify/frontend/hooks/useI18nWithLanguageService";
import { useRouteServiceWithNavigate } from "../../../../../io/hyperify/frontend/hooks/useRouteServiceWithNavigate";
import { useStoreProductList } from "../../../../../io/hyperify/frontend/hooks/useStoreProductList";
import {
    DOMAINS_ENABLED,
    SOFTWARE_ENABLED,
} from "../../../core/constants/company";
import {
    ABOUT_ROUTE,
    AGENT_FACEBOOK_ROUTE,
    ATLAS_EXTRA_ROUTE,
    ATLAS_ROUTE,
    CART_ROUTE,
    COOKIES_ROUTE,
    CUSTOMER_SERVICE_ROUTE,
    DATABASES_ROUTE,
    DOMAIN_SALE_ROUTE,
    DOMAINS_ROUTE,
    DOMAINS_SALE_LIST_ROUTE,
    EMAILS_EXTRA_PRODUCT_LIST_ROUTE,
    EMAILS_EXTRA_ROUTE,
    EMAILS_FOOTER_ROUTE,
    EMAILS_ROUTE,
    FOOTER_ROUTE,
    IMAGES_ROUTE,
    INDEX_ROUTE,
    INTRO_ROUTE,
    MY_CLIENT_INVENTORY_ITEM_ROUTE,
    MY_CLIENT_INVENTORY_LIST_ROUTE,
    MY_CLIENT_INVOICE_LIST_ROUTE,
    MY_CLIENT_INVOICE_ROUTE,
    MY_CLIENT_LIST_ROUTE,
    MY_CLIENT_ORDER_LIST_ROUTE,
    MY_CLIENT_PAYMENT_LIST_ROUTE,
    MY_CLIENT_PAYMENT_ROUTE,
    MY_CLIENT_ROUTE,
    MY_INDEX_ROUTE,
    MY_LOGIN_ROUTE,
    NEW_ORDER_ROUTE,
    NEWSLETTERS_ROUTE,
    OUTLET_PRODUCT_ROUTE,
    OUTLET_ROUTE,
    PREMIUM_DOMAINS_ROUTE,
    PRIVACY_POLICY_ROUTE,
    SHELLS_EXTRA_ROUTE,
    SHELLS_FEATURE_LIST_ROUTE,
    SHELLS_FOOTER_ROUTE,
    SHELLS_ROUTE,
    SOFTWARE_FOOTER_ROUTE,
    SOFTWARE_ROUTE,
    TERMS_OF_DELIVERY_ROUTE,
    TERMS_OF_USE_ROUTE,
    VIRTUAL_SERVER_EXTRA_ROUTE,
    VIRTUALSERVER_FOOTER_ROUTE,
    VIRTUALSERVERS_EXTRA_ROUTE,
    VIRTUALSERVERS_FAQ_ROUTE,
    VIRTUALSERVERS_INTRO_ROUTE,
    VIRTUALSERVERS_PRODUCT_ROUTE,
    VIRTUALSERVERS_ROUTE,
    WEB_HOTEL_EXTRA_ROUTE,
    WEBHOTELS_EXTRA_ROUTE,
    WEBHOTELS_FOOTER_ROUTE,
    WEBHOTELS_ROUTE,
    WEBRTC_ROUTE,
    WORDPRESS_ROUTE,
} from "../../../core/constants/route";
import {
    ALL_ATLAS_EXTRA_PRODUCT_IDS,
    ALL_EMAIL_EXTRA_PRODUCT_IDS,
    ALL_VPS_EXTRA_PRODUCT_IDS,
    ALL_WEB_EXTRA_PRODUCT_IDS,
    SendanorProductId,
} from "../../../core/types/SendanorProductId";

// Views (see note above!)
import { MyDashboardMenu } from "../common/myDashboardMenu/MyDashboardMenu";

// NOTE! Order in which these imports are done is essential -- it declares the order of SCSS files!
// So, put components before layouts, and layouts before views. Otherwise layout's SCSS files will
// overwrite SCSS from views and make your life harder.
// Layouts (see note above!)
import { DashboardLayout } from "../layouts/dashboard/DashboardLayout";
import { MainLayout } from "../layouts/main/MainLayout";
import { AgentFacebookView } from "../views/agentFacebookView/AgentFacebookView";
import { CartView } from "../views/cartView/CartView";
import { CookiesView } from "../views/cookiesView/CookiesView";
import { CustomerServiceView } from "../views/customerServiceView/CustomerServiceView";
import { MainIndexView } from "../views/main/MainIndexView";
import { MyClientView } from "../views/my/client/MyClientView";
import { MyClientListView } from "../views/my/clientList/MyClientListView";
import { MyFuturePaymentListView } from "../views/my/futurePaymentList/MyFuturePaymentListView";
import { MyInventoryItemView } from "../views/my/inventoryItem/MyInventoryItemView";
import { MyInventoryListView } from "../views/my/inventoryList/MyInventoryListView";
import { MyInvoiceView } from "../views/my/invoice/MyInvoiceView";
import { MyInvoiceListView } from "../views/my/invoiceList/MyInvoiceListView";
import { MyOrderView } from "../views/my/newOrder/MyOrderView";
import { MyOrderListView } from "../views/my/orderList/MyOrderListView";
import { MyPaymentView } from "../views/my/payment/MyPaymentView";
import { MyLoginView } from "../views/my/unauthorized/MyLoginView";
import { NewsLetterView } from "../views/newsLetterView/NewsLetterView";
import { NotFoundView } from "../views/notFoundView/NotFoundView";
import { PrivacyPolicyView } from "../views/privacyPolicyView/PrivacyPolicyView";
import { AtlasIndexView } from "../views/products/atlas/AtlasIndexView";
import { DomainIndexView } from "../views/products/domain/DomainIndexView";
import { EmailIndexView } from "../views/products/email/EmailIndexView";
import { ImageIndexView } from "../views/products/image/ImageIndexView";
import { OutletIndexView } from "../views/products/outlet/OutletIndexView";
import { PremiumIndexView } from "../views/products/premiumDomain/PremiumIndexView";
import { ShellIndexView } from "../views/products/shell/ShellIndexView";
import { SoftwareIndexView } from "../views/products/software/SoftwareIndexView";
import { VirtualServerIndexView } from "../views/products/virtualServer/VirtualServerIndexView";
import { WebHotelIndexView } from "../views/products/webHotel/WebHotelIndexView";
import { WebRTCIndexView } from "../views/products/webRTC/WebRTCIndexView";
import { WordPressIndexView } from "../views/products/wordPress/WordPressIndexView";
import { TermsOfDeliveryView } from "../views/termsOfDeleveryView/TermsOfDeliveryView";
import { TermsOfUseView } from "../views/termsOfUseView/TermsOfUseView";

const LOG = LogService.createLogger('SendanorFiApp');

/**
 * The name for this component is complete legacy.
 *
 * It should be something like "FiSendanorApp" now :)
 *
 * It is the app of sendanor.fi.
 *
 */
export function SendanorFiApp () {

    const { t, i18n } = useTranslation();

    useRouteServiceWithNavigate();
    useDefaultLanguage(i18n?.language);
    useI18nWithLanguageService();

    const outletProductList = useStoreProductList(ProductType.OUTLET);

    const myRoutes = {
        path: MY_INDEX_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <DashboardLayout nav={(
                    <MyDashboardMenu t={t as TranslationFunction} />
                )} >
                    <Outlet />
                </DashboardLayout>
            </MainLayout>
        ),
        children: [
            {path: MY_LOGIN_ROUTE, element: <MyLoginView t={t as TranslationFunction} />},
            {path: MY_CLIENT_ROUTE, element: <MyClientView t={t as TranslationFunction} />},
            {path: MY_CLIENT_INVOICE_ROUTE, element: <MyInvoiceView t={t as TranslationFunction} />},
            {path: MY_CLIENT_ORDER_LIST_ROUTE, element: <MyOrderListView t={t as TranslationFunction} />},
            {path: MY_CLIENT_INVENTORY_ITEM_ROUTE, element: <MyInventoryItemView t={t as TranslationFunction} />},
            {path: MY_CLIENT_INVENTORY_LIST_ROUTE, element: <MyInventoryListView t={t as TranslationFunction} />},
            {path: MY_CLIENT_PAYMENT_ROUTE, element: <MyPaymentView t={t as TranslationFunction} />},
            {path: MY_CLIENT_PAYMENT_LIST_ROUTE, element: <MyFuturePaymentListView t={t as TranslationFunction} />},
            {path: MY_CLIENT_INVOICE_LIST_ROUTE, element: <MyInvoiceListView t={t as TranslationFunction} />},

            {path: '*', element: <Navigate to={MY_CLIENT_LIST_ROUTE} />},
            {path: MY_INDEX_ROUTE, element: <Navigate to={MY_CLIENT_LIST_ROUTE} />}
        ]
    };

    const vpsRoutes = {
        path: VIRTUALSERVERS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [

            {path: VIRTUALSERVERS_ROUTE, element: <VirtualServerIndexView t={t as TranslationFunction} />},
            {path: VIRTUALSERVERS_INTRO_ROUTE, element: <VirtualServerIndexView t={t as TranslationFunction} />},
            {path: VIRTUALSERVERS_PRODUCT_ROUTE, element: <VirtualServerIndexView t={t as TranslationFunction} />},

            ...(
                map(
                    ALL_VPS_EXTRA_PRODUCT_IDS,
                    (product : SendanorProductId) => ({
                        path: VIRTUAL_SERVER_EXTRA_ROUTE(product),
                        element: <VirtualServerIndexView t={t as TranslationFunction} />,
                    })
                )
            ),

            {path: VIRTUALSERVERS_EXTRA_ROUTE, element: <VirtualServerIndexView t={t as TranslationFunction} />},
            {path: VIRTUALSERVERS_FAQ_ROUTE, element: <VirtualServerIndexView t={t as TranslationFunction} />},
            {path: VIRTUALSERVER_FOOTER_ROUTE, element: <VirtualServerIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: VIRTUALSERVERS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},

        ]
    };

    const atlasRoutes = {
        path: ATLAS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: ATLAS_ROUTE, element: <AtlasIndexView t={t as TranslationFunction} />},
            ...(
                map(
                    ALL_ATLAS_EXTRA_PRODUCT_IDS,
                    (product : SendanorProductId) => ({
                        path: ATLAS_EXTRA_ROUTE(product),
                        element: <AtlasIndexView t={t as TranslationFunction} />,
                    })
                )
            ),
            {path: '*', element: <NotFoundView t={t} />},
            {path: ATLAS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const webhotelRoutes = {
        path: WEBHOTELS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            ...(
                map(
                    ALL_WEB_EXTRA_PRODUCT_IDS,
                    (product : SendanorProductId) => ({
                        path: WEB_HOTEL_EXTRA_ROUTE(product),
                        element: <WebHotelIndexView t={t as TranslationFunction} />,
                    })
                )
            ),
            {path: WEBHOTELS_ROUTE, element: <WebHotelIndexView t={t as TranslationFunction} />},
            {path: WEBHOTELS_EXTRA_ROUTE, element: <WebHotelIndexView t={t as TranslationFunction} />},
            {path: WEBHOTELS_FOOTER_ROUTE, element: <WebHotelIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: WEBHOTELS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const emailRoutes = {
        path: EMAILS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            ...(
                map(
                    ALL_EMAIL_EXTRA_PRODUCT_IDS,
                    (product : SendanorProductId) => ({
                        path: EMAILS_EXTRA_ROUTE(product),
                        element: <EmailIndexView t={t as TranslationFunction} />,
                    })
                )
            ),
            {path: EMAILS_EXTRA_PRODUCT_LIST_ROUTE, element: <EmailIndexView t={t as TranslationFunction} />},
            {path: EMAILS_FOOTER_ROUTE, element: <EmailIndexView t={t as TranslationFunction} />},
            {path: EMAILS_ROUTE, element: <EmailIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: EMAILS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const domainProductList = useStoreProductList( ProductType.DOMAIN );
    LOG.debug( `domainProductList = `, domainProductList );

    const domainRoutes = {
        path: DOMAINS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [

            ...(
                map(
                    domainProductList,
                    (product : Product) => ({
                        path: DOMAIN_SALE_ROUTE(product.id),
                        element: <DomainIndexView t={t as TranslationFunction} />,
                    })
                )
            ),

            {path: DOMAINS_ROUTE, element: <DomainIndexView t={t as TranslationFunction} />},
            {path: DOMAINS_SALE_LIST_ROUTE, element: <DomainIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: DOMAINS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const shellRoutes = {
        path: SHELLS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: SHELLS_ROUTE, element: <ShellIndexView t={t as TranslationFunction} />},
            {path: SHELLS_FEATURE_LIST_ROUTE, element: <ShellIndexView t={t as TranslationFunction} />},
            {path: SHELLS_EXTRA_ROUTE, element: <ShellIndexView t={t as TranslationFunction} />},
            {path: SHELLS_FOOTER_ROUTE, element: <ShellIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: SHELLS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const softwareRoutes = {
        path: SOFTWARE_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: SOFTWARE_ROUTE, element: <SoftwareIndexView t={t as TranslationFunction} />},
            {path: SOFTWARE_FOOTER_ROUTE, element: <SoftwareIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: SOFTWARE_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const wpRoutes = {
        path: WORDPRESS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: WORDPRESS_ROUTE, element: <WordPressIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: WORDPRESS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    // const netRoutes = {
    //     path: NET_ROUTE,
    //     element: (
    //         <MainLayout t={t as TranslationFunction}>
    //             <Outlet />
    //         </MainLayout>
    //     ),
    //     children: [
    //         {path: NET_ROUTE, element: <NetIndexView t={t as TranslationFunction} />},
    //         {path: '*', element: <NotFoundView t={t} />},
    //         {path: NET_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
    //     ]
    // };

    const webrtcRoutes = {
        path: WEBRTC_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: WEBRTC_ROUTE, element: <WebRTCIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: WEBRTC_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const premiumRoutes = {
        path: PREMIUM_DOMAINS_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: PREMIUM_DOMAINS_ROUTE, element: <PremiumIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t}/>},
            {path: PREMIUM_DOMAINS_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const imageRoutes = {
        path: IMAGES_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: IMAGES_ROUTE, element: <ImageIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: IMAGES_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const outletRoutes = {
        path: OUTLET_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            ...(
                map(
                    outletProductList,
                    (product : Product) => ({
                        path: OUTLET_PRODUCT_ROUTE(product.id),
                        element: <OutletIndexView t={t as TranslationFunction} />,
                    })
                )
            ),
            {path: OUTLET_ROUTE, element: <OutletIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: OUTLET_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const mainRoutes = {
        path: INDEX_ROUTE,
        element: (
            <MainLayout t={t as TranslationFunction}>
                <Outlet />
            </MainLayout>
        ),
        children: [
            {path: INDEX_ROUTE, element: <MainIndexView t={t as TranslationFunction} />},
            {path: ABOUT_ROUTE, element: <MainIndexView t={t as TranslationFunction} />},
            {path: INTRO_ROUTE, element: <MainIndexView t={t as TranslationFunction} />},
            {path: NEW_ORDER_ROUTE, element: <MyOrderView t={t as TranslationFunction} />},
            {path: DATABASES_ROUTE, element: <MainIndexView t={t as TranslationFunction} />},
            {path: MY_CLIENT_LIST_ROUTE, element: <MyClientListView t={t as TranslationFunction} />},
            {path: CUSTOMER_SERVICE_ROUTE, element: <CustomerServiceView t={t as TranslationFunction} />},
            {path: CART_ROUTE, element: <CartView t={t as TranslationFunction} />},
            {path: NEWSLETTERS_ROUTE, element: <NewsLetterView t={t as TranslationFunction} />},
            {path: PRIVACY_POLICY_ROUTE, element: <PrivacyPolicyView t={t} />},
            {path: TERMS_OF_DELIVERY_ROUTE, element: <TermsOfDeliveryView t={t} />},
            {path: TERMS_OF_USE_ROUTE, element: <TermsOfUseView t={t} />},
            {path: COOKIES_ROUTE, element: <CookiesView t={t as TranslationFunction} />},
            {path: AGENT_FACEBOOK_ROUTE, element: <AgentFacebookView t={t as TranslationFunction} />},
            {path: FOOTER_ROUTE, element: <MainIndexView t={t as TranslationFunction} />},
            {path: '*', element: <NotFoundView t={t} />},
            {path: INDEX_ROUTE, element: <Navigate to={INDEX_ROUTE} />},
        ]
    };

    const routing = useRoutes(
        [
            atlasRoutes,
            vpsRoutes,
            webhotelRoutes,
            emailRoutes,
            ...(DOMAINS_ENABLED ? [
                domainRoutes,
            ] : []),
            shellRoutes,
            ...(SOFTWARE_ENABLED ? [
                softwareRoutes,
            ] : []),
            wpRoutes,
            // netRoutes,
            outletRoutes,
            webrtcRoutes,
            premiumRoutes,
            imageRoutes,
            mainRoutes,
            myRoutes
        ]
    );

    return <>{routing}</>;

}
